import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Shield, TrendingUp, Users, Cloud, Compass, Database } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Questions from sustainify-resilience-framework-GoldenGirl.xlsx (1–5 maturity scale)
const questions = [
  {
    id: 'q1',
    domain: 'Foresight & Sensing',
    question: 'How does your organisation identify emerging risks, disruptions, and opportunities?',
    options: [
      { score: 1, label: 'No process — we find out when things happen' },
      { score: 2, label: 'Informal — networks and news only' },
      { score: 3, label: 'Periodic review of reports and trends' },
      { score: 4, label: 'Structured scanning with regular scenario reviews' },
      { score: 5, label: 'Formal horizon scanning — we shape future narratives' },
    ],
  },
  {
    id: 'q2',
    domain: 'Foresight & Sensing',
    question: 'When a major disruption hits, how well can your organisation adapt and absorb the shock?',
    options: [
      { score: 1, label: 'We react as events unfold — usually chaotic' },
      { score: 2, label: 'Some plans exist but we often scramble' },
      { score: 3, label: 'We activate playbooks with moderate success' },
      { score: 4, label: 'We respond quickly using pre-designed scenarios' },
      { score: 5, label: 'We anticipated it — we were already prepared' },
    ],
  },
  {
    id: 'q3',
    domain: 'Climate Exposure',
    question: 'How well does your organisation understand its climate-related risks — physical, regulatory, and transitional?',
    options: [
      { score: 1, label: "Not assessed — we haven't looked at this" },
      { score: 2, label: 'Basic awareness, no structured analysis' },
      { score: 3, label: 'Some analysis done, incomplete or outdated' },
      { score: 4, label: 'Current, comprehensive assessment in place' },
      { score: 5, label: 'Best-practice assessment — we support others' },
    ],
  },
  {
    id: 'q4',
    domain: 'Climate Exposure',
    question: 'Does your organisation have a climate adaptation or transition strategy?',
    options: [
      { score: 1, label: 'No — not on our agenda' },
      { score: 2, label: 'In early development' },
      { score: 3, label: 'A plan exists but needs updating or resourcing' },
      { score: 4, label: 'Current, resourced, and being implemented' },
      { score: 5, label: 'Best-practice strategy — we help others develop theirs' },
    ],
  },
  {
    id: 'q5',
    domain: 'Operational Agility',
    question: 'How quickly can your organisation adapt its programs, services, or operations when circumstances change?',
    options: [
      { score: 1, label: 'Very slowly — change takes months or years' },
      { score: 2, label: 'With difficulty — often met with resistance' },
      { score: 3, label: 'Within weeks, with reasonable effort' },
      { score: 4, label: 'Fast — agility is built into how we operate' },
      { score: 5, label: 'Exemplar — we lead our sector in responsiveness' },
    ],
  },
  {
    id: 'q6',
    domain: 'Operational Agility',
    question: 'Do you have a balance of structured planning and the ability to dynamically pivot and change direction?',
    options: [
      { score: 1, label: 'Rigid — structured with little room to adapt' },
      { score: 2, label: 'Mostly structured, occasional pivots only' },
      { score: 3, label: 'Some balance but not consistently maintained' },
      { score: 4, label: 'Well-balanced — plan and adapt with confidence' },
      { score: 5, label: 'Dynamic by design — plan and pivot seamlessly' },
    ],
  },
  {
    id: 'q7',
    domain: 'Org & Data Maturity',
    question: "How effective are your organisation's systems, processes, and infrastructure?",
    options: [
      { score: 1, label: 'Significant gaps — legacy systems hold us back' },
      { score: 2, label: 'Partial — known gaps remain unresolved' },
      { score: 3, label: 'Mostly effective, with some improvement areas' },
      { score: 4, label: 'Well-integrated and regularly reviewed' },
      { score: 5, label: 'Fully optimised — a competitive advantage' },
    ],
  },
  {
    id: 'q8',
    domain: 'Org & Data Maturity',
    question: 'How well does your organisation use data visibility and trust to make informed and timely decisions?',
    options: [
      { score: 1, label: 'We rely mostly on experience and intuition' },
      { score: 2, label: 'Some data used but inconsistent across teams' },
      { score: 3, label: 'Data used regularly but quality and access vary' },
      { score: 4, label: 'Data and evidence central to all decisions' },
      { score: 5, label: 'Data-led at all levels — analytics drives strategy' },
    ],
  },
  {
    id: 'q9',
    domain: 'People & Culture',
    question: 'How would you describe staff confidence, wellbeing, and capacity in the face of ongoing uncertainty and change?',
    options: [
      { score: 1, label: 'Anxious and disengaged — change feels threatening' },
      { score: 2, label: 'Mixed — some cope, others are struggling' },
      { score: 3, label: 'Generally stable, with pockets of stress' },
      { score: 4, label: 'High — our culture equips people for change' },
      { score: 5, label: 'Exceptional — our people thrive through disruption' },
    ],
  },
  {
    id: 'q10',
    domain: 'People & Culture',
    question: 'How embedded is a learning, failure, and adaptation mindset across your organisation?',
    options: [
      { score: 1, label: 'Not present — failure is not discussed openly' },
      { score: 2, label: 'Emerging in some teams or leaders' },
      { score: 3, label: 'Growing — leadership actively promotes it' },
      { score: 4, label: 'Deeply embedded — learning is the norm' },
      { score: 5, label: 'We lead sector-wide learning and capability' },
    ],
  },
];

const domainIcons = {
  'Foresight & Sensing': <Compass size={24} />,
  'Climate Exposure': <Cloud size={24} />,
  'Operational Agility': <TrendingUp size={24} />,
  'Org & Data Maturity': <Database size={24} />,
  'People & Culture': <Users size={24} />,
};

// Quadrant / band messaging from Sheet 4 of the framework spreadsheet
const bandMessages = {
  VULNERABLE: {
    label: 'Vulnerable',
    icon: <AlertCircle size={32} />,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-500',
    message:
      "Your results show significant gaps across multiple resilience domains. Your organisation is under pressure it currently lacks the capability to fully absorb. The good news: awareness is the first step — and there is a clear path forward. We recommend starting with a Resilience Diagnostic — a focused 2–3 week engagement to identify your highest-priority gaps and build a clear, actionable roadmap.",
    cta: 'Book your Resilience Diagnostic',
  },
  EMERGING: {
    label: 'Emerging',
    icon: <AlertCircle size={32} />,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-500',
    message:
      'Your organisation is building resilience — capability is developing across domains, but meaningful gaps remain that will compound as external pressure grows. The window to build ahead of the wave is now. A Resilience Diagnostic will help you identify where to focus and build a targeted improvement roadmap while you have time and space to do it well.',
    cta: 'Start your Resilience Diagnostic',
  },
  ADAPTIVE: {
    label: 'Adaptive',
    icon: <Shield size={32} />,
    color: 'text-yellow-700',
    bg: 'bg-yellow-50',
    border: 'border-yellow-500',
    message:
      'Your organisation shows solid resilience foundations with clear strengths — and some specific areas of exposure that, if addressed, would significantly lift your overall position. You are well-placed to lead through change. The opportunity is to sustain your strengths and close remaining gaps before they become vulnerabilities. Strategic advisory support can help you stay ahead and extend your advantage.',
    cta: 'Explore our advisory options',
  },
  PREPARED: {
    label: 'Prepared',
    icon: <CheckCircle2 size={32} />,
    color: 'text-kabi-teal',
    bg: 'bg-kabi-teal/10',
    border: 'border-kabi-teal',
    message:
      "Strong performance across all five resilience domains. Your organisation is well-positioned to navigate uncertainty, absorb disruption, and lead through change. The focus now is on maintaining this edge, scanning ahead for emerging exposures, and extending your capability across your sector and supply chain. We'd love to explore how Sustainify can support your ongoing foresight and advisory needs.",
    cta: 'Explore our strategic advisory retainer',
  },
};

// Domain-level messaging by maturity label (from Sheet 4)
const domainLevelCopy = {
  Unaware: 'Limited or no formal capability. Highest-priority area for development.',
  Developing:
    'Early-stage capability emerging. Inconsistent and informal. Focused investment would lift this significantly.',
  Defined:
    'Structured approaches in place. Opportunity to deepen integration and measure outcomes.',
  Managed:
    'Strong, managed capability. Use this as a foundation to lead in adjacent areas.',
  Optimised:
    'Sector-leading strength. Leverage as competitive advantage and support others.',
};

const getDomainColor = (pct) => {
  if (pct < 40) return 'bg-red-500';
  if (pct < 65) return 'bg-yellow-500';
  return 'bg-kabi-teal';
};

const Quiz = () => {
  const [step, setStep] = useState('intro'); // intro | quiz | leadgate | results
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({
    first_name: '',
    last_name: '',
    email: '',
    organisation: '',
    role: '',
    sector: '',
    consent: false,
  });
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const totalQuestions = questions.length;
  const progress = ((currentQ + 1) / totalQuestions) * 100;

  const handleSelectAnswer = (score) => {
    const q = questions[currentQ];
    const newAnswers = { ...answers, [q.id]: score };
    setAnswers(newAnswers);
    // Auto-advance
    setTimeout(() => {
      if (currentQ < totalQuestions - 1) {
        setCurrentQ(currentQ + 1);
      } else {
        setStep('leadgate');
      }
    }, 300);
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/quiz/submit`, {
        ...lead,
        answers,
      });
      setResults(response.data);
      setStep('results');
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const startQuiz = () => {
    setStep('quiz');
    setCurrentQ(0);
    window.scrollTo(0, 0);
  };

  // ==================== INTRO SCREEN ====================
  if (step === 'intro') {
    return (
      <div data-testid="quiz-intro" className="min-h-screen bg-ozone-white">
        <Helmet>
          <title>Organisational Resilience Quiz | Sustainify® | How Resilient is Your Organisation?</title>
          <meta name="description" content="Take the 3-minute Sustainify® Organisational Resilience Quiz. Understand your vulnerability to uncertainty, climate change, and disruption. Get your instant score & personalised recommendations." />
        </Helmet>

        {/* Hero */}
        <section 
          className="relative py-24 text-ozone-white overflow-hidden"
          style={{
            backgroundImage: 'linear-gradient(rgba(15, 46, 31, 0.95), rgba(8, 32, 26, 0.98)), url(https://images.unsplash.com/photo-1451187580459-43490279c0fa)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <span className="inline-block bg-solar-kinetic/20 text-solar-kinetic px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
              Free · 3 Minutes · Instant Results
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-testid="quiz-title">
              How resilient is <span className="text-solar-kinetic">your organisation?</span>
            </h1>
            <p className="text-xl md:text-2xl text-ozone-white/90 mb-8 leading-relaxed">
              Discover your vulnerability to uncertainty, complexity, climate change, and rapid disruption — in just 3 minutes.
            </p>
            <button
              onClick={startQuiz}
              data-testid="start-quiz-btn"
              className="inline-flex items-center space-x-2 bg-solar-kinetic text-basalt-carbon px-10 py-5 rounded-full font-bold text-lg hover:bg-solar-kinetic/90 transition-all shadow-xl"
            >
              <span>Start the Quiz</span>
              <ArrowRight size={22} />
            </button>
          </div>
        </section>

        {/* What You'll Discover */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-basalt-carbon mb-4">
                What You'll <span className="text-kabi-teal">Discover</span>
              </h2>
              <p className="text-lg text-basalt-carbon/70 max-w-2xl mx-auto">
                A snapshot of your organisation's resilience across 5 critical domains.
              </p>
            </div>
            <div className="grid md:grid-cols-5 gap-4">
              {['Foresight & Sensing', 'Climate Exposure', 'Operational Agility', 'Org & Data Maturity', 'People & Culture'].map((d) => (
                <div key={d} className="bg-ozone-white border-2 border-kabi-teal/20 rounded-xl p-6 text-center hover:border-kabi-teal hover:shadow-lg transition-all" data-testid={`domain-preview-${d.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                  <div className="text-kabi-teal mb-3 flex justify-center">
                    {domainIcons[d]}
                  </div>
                  <h3 className="text-sm font-bold text-basalt-carbon leading-tight">{d}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16 bg-kabi-teal/5">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-xl text-basalt-carbon/85 leading-relaxed mb-6 italic">
              "In times of unprecedented change, resilience isn't optional — it's the foundation of every thriving organisation."
            </p>
            <p className="text-base text-basalt-carbon/70">
              — <strong>Rachelle Cooper Kulkarni</strong>, Founder & Principal Consultant, Sustainify®
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-basalt-carbon mb-6">
              Ready to see your <span className="text-kabi-teal">resilience score?</span>
            </h2>
            <p className="text-lg text-basalt-carbon/70 mb-8">
              10 quick questions. Instant results. Actionable next steps.
            </p>
            <button
              onClick={startQuiz}
              data-testid="start-quiz-btn-bottom"
              className="inline-flex items-center space-x-2 bg-kabi-teal text-ozone-white px-10 py-5 rounded-full font-bold text-lg hover:bg-kabi-teal/90 transition-all"
            >
              <span>Take the Quiz</span>
              <ArrowRight size={22} />
            </button>
          </div>
        </section>
      </div>
    );
  }

  // ==================== QUIZ SCREEN ====================
  if (step === 'quiz') {
    const q = questions[currentQ];
    return (
      <div data-testid="quiz-in-progress" className="min-h-screen bg-ozone-white flex flex-col">
        <Helmet><title>{`Question ${currentQ + 1} of ${totalQuestions} | Sustainify® Resilience Quiz`}</title></Helmet>

        {/* Progress Bar */}
        <div className="bg-white border-b border-basalt-carbon/10 sticky top-0 z-40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-kabi-teal">Question {currentQ + 1} of {totalQuestions}</span>
              <span className="text-sm text-basalt-carbon/60">{Math.round(progress)}% complete</span>
            </div>
            <div className="w-full bg-basalt-carbon/10 rounded-full h-2 overflow-hidden">
              <div
                className="bg-solar-kinetic h-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
                data-testid="progress-bar"
              />
            </div>
          </div>
        </div>

        <div className="flex-grow flex items-center justify-center py-12">
          <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-3">
              <span className="inline-flex items-center space-x-2 bg-kabi-teal/10 text-kabi-teal px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                {domainIcons[q.domain]}
                <span>{q.domain}</span>
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-basalt-carbon mb-8 leading-tight" data-testid={`question-${currentQ + 1}`}>
              {q.question}
            </h2>

            <div className="space-y-3">
              {q.options.map((opt, idx) => {
                const isSelected = answers[q.id] === opt.score;
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelectAnswer(opt.score)}
                    data-testid={`option-${idx + 1}`}
                    className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-kabi-teal bg-kabi-teal/10 shadow-md'
                        : 'border-basalt-carbon/15 bg-white hover:border-kabi-teal/50 hover:bg-kabi-teal/5'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-4 mt-0.5 flex-shrink-0 ${
                        isSelected ? 'border-kabi-teal bg-kabi-teal' : 'border-basalt-carbon/30'
                      }`}>
                        {isSelected && <CheckCircle2 size={16} className="text-white" />}
                      </div>
                      <span className={`text-base ${isSelected ? 'text-basalt-carbon font-semibold' : 'text-basalt-carbon/85'}`}>
                        {opt.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Back button */}
            {currentQ > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setCurrentQ(currentQ - 1)}
                  data-testid="back-btn"
                  className="inline-flex items-center space-x-2 text-basalt-carbon/60 hover:text-kabi-teal transition-colors"
                >
                  <ArrowLeft size={18} />
                  <span>Back</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==================== LEAD GATE ====================
  if (step === 'leadgate') {
    return (
      <div data-testid="lead-gate" className="min-h-screen bg-ozone-white py-16">
        <Helmet><title>Get Your Score | Sustainify® Resilience Quiz</title></Helmet>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-solar-kinetic rounded-full mb-4">
                <CheckCircle2 size={32} className="text-basalt-carbon" />
              </div>
              <h2 className="text-3xl font-bold text-basalt-carbon mb-3">Almost there!</h2>
              <p className="text-lg text-basalt-carbon/70 leading-relaxed">
                Tell us where to send your personalised resilience report.
              </p>
            </div>

            <form onSubmit={handleSubmitLead} className="space-y-4" data-testid="lead-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-basalt-carbon mb-2">First Name *</label>
                  <input
                    type="text"
                    required
                    value={lead.first_name}
                    onChange={(e) => setLead({ ...lead, first_name: e.target.value })}
                    data-testid="input-first-name"
                    className="w-full px-4 py-3 border-2 border-basalt-carbon/15 rounded-lg focus:border-kabi-teal outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-basalt-carbon mb-2">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={lead.last_name}
                    onChange={(e) => setLead({ ...lead, last_name: e.target.value })}
                    data-testid="input-last-name"
                    className="w-full px-4 py-3 border-2 border-basalt-carbon/15 rounded-lg focus:border-kabi-teal outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-basalt-carbon mb-2">Work Email *</label>
                <input
                  type="email"
                  required
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  data-testid="input-email"
                  className="w-full px-4 py-3 border-2 border-basalt-carbon/15 rounded-lg focus:border-kabi-teal outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-basalt-carbon mb-2">Organisation *</label>
                <input
                  type="text"
                  required
                  value={lead.organisation}
                  onChange={(e) => setLead({ ...lead, organisation: e.target.value })}
                  data-testid="input-organisation"
                  className="w-full px-4 py-3 border-2 border-basalt-carbon/15 rounded-lg focus:border-kabi-teal outline-none transition-colors"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-basalt-carbon mb-2">Role / Title</label>
                  <input
                    type="text"
                    value={lead.role}
                    onChange={(e) => setLead({ ...lead, role: e.target.value })}
                    data-testid="input-role"
                    className="w-full px-4 py-3 border-2 border-basalt-carbon/15 rounded-lg focus:border-kabi-teal outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-basalt-carbon mb-2">Sector</label>
                  <select
                    value={lead.sector}
                    onChange={(e) => setLead({ ...lead, sector: e.target.value })}
                    data-testid="input-sector"
                    className="w-full px-4 py-3 border-2 border-basalt-carbon/15 rounded-lg focus:border-kabi-teal outline-none transition-colors bg-white"
                  >
                    <option value="">Select sector...</option>
                    <option value="Government">Government</option>
                    <option value="Energy & Utilities">Energy & Utilities</option>
                    <option value="Infrastructure">Infrastructure</option>
                    <option value="Not-for-Profit">Not-for-Profit</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Financial Services">Financial Services</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Education">Education</option>
                    <option value="Mining & Resources">Mining & Resources</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="pt-2">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={lead.consent}
                    onChange={(e) => setLead({ ...lead, consent: e.target.checked })}
                    data-testid="input-consent"
                    className="mt-1 w-5 h-5 accent-kabi-teal"
                  />
                  <span className="text-sm text-basalt-carbon/75 leading-relaxed">
                    I agree to receive my personalised resilience report and occasional insights from Sustainify®. You can unsubscribe anytime. *
                  </span>
                </label>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-500 text-red-700 p-4 rounded-lg text-sm" data-testid="error-message">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-quiz-btn"
                className="w-full inline-flex items-center justify-center space-x-2 bg-kabi-teal text-ozone-white px-8 py-4 rounded-full font-bold text-lg hover:bg-kabi-teal/90 transition-all disabled:opacity-50"
              >
                {submitting ? (
                  <span>Calculating your score...</span>
                ) : (
                  <>
                    <span>See My Results</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ==================== RESULTS SCREEN ====================
  if (step === 'results' && results) {
    const bandInfo = bandMessages[results.band] || bandMessages.EMERGING;
    const domainLevels = results.domain_levels || {};
    return (
      <div data-testid="quiz-results" className="min-h-screen bg-ozone-white">
        <Helmet><title>Your Resilience Score | Sustainify®</title></Helmet>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Big Score */}
          <div className="text-center mb-12">
            <p className="text-sm font-bold uppercase tracking-widest text-kabi-teal mb-4">Your Organisational Resilience Score</p>
            <div className="mb-6">
              <div className="text-7xl md:text-8xl font-bold text-basalt-carbon mb-2" data-testid="resilience-score">
                {results.resilience_pct}<span className="text-3xl text-basalt-carbon/50">%</span>
              </div>
              <p className="text-lg text-basalt-carbon/70">
                Resilience · <span className="text-red-600 font-semibold">{results.vulnerability_pct}% Vulnerability</span>
              </p>
            </div>

            <div className={`inline-flex items-center space-x-3 ${bandInfo.bg} ${bandInfo.border} border-2 px-6 py-3 rounded-full`}>
              <span className={bandInfo.color}>{bandInfo.icon}</span>
              <span className={`font-bold text-lg ${bandInfo.color}`} data-testid="band-label">{bandInfo.label}</span>
            </div>

            <p className="text-lg text-basalt-carbon/80 mt-6 max-w-2xl mx-auto leading-relaxed" data-testid="band-message">
              {bandInfo.message}
            </p>
          </div>

          {/* Domain Breakdown */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="domain-breakdown">
            <h3 className="text-2xl font-bold text-basalt-carbon mb-6">Domain Breakdown</h3>
            <div className="space-y-6">
              {Object.entries(results.domain_scores).map(([domain, pct]) => {
                const level = domainLevels[domain];
                return (
                  <div key={domain} data-testid={`domain-${domain.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-kabi-teal">{domainIcons[domain]}</span>
                        <span className="font-semibold text-basalt-carbon">{domain}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        {level && (
                          <span className="text-xs font-bold uppercase tracking-wider text-basalt-carbon/60">
                            {level}
                          </span>
                        )}
                        <span className="font-bold text-basalt-carbon">{pct}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-basalt-carbon/10 rounded-full h-3 overflow-hidden mb-2">
                      <div
                        className={`${getDomainColor(pct)} h-full transition-all duration-1000 ease-out`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {level && domainLevelCopy[level] && (
                      <p className="text-sm text-basalt-carbon/70 leading-relaxed">
                        {domainLevelCopy[level]}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-sm text-basalt-carbon/70 mt-8 pt-6 border-t border-basalt-carbon/10 italic">
              <strong className="text-kabi-teal">Focus area:</strong> {results.lowest_domain} — your lowest-scoring domain represents the biggest opportunity for uplift.
            </p>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-basalt-carbon to-kabi-teal rounded-2xl p-10 text-ozone-white text-center shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Next Step for Your Organisation</h3>
            <p className="text-lg text-ozone-white/90 mb-6 max-w-2xl mx-auto leading-relaxed">
              Get personalised guidance to strengthen your weakest domains and build lasting resilience.
            </p>
            <a
              href={`mailto:info@sustainify.com.au?subject=${encodeURIComponent('Resilience Diagnostic Request - ' + bandInfo.cta)}&body=${encodeURIComponent(`Hi Rachelle,\n\nI recently took the Sustainify Resilience Quiz.\n\nMy score: ${results.resilience_pct}% (${bandInfo.label})\nLowest-scoring domain: ${results.lowest_domain}\n\nI'd like to explore: ${bandInfo.cta}\n\nThanks!`)}`}
              data-testid="results-cta"
              className="inline-flex items-center space-x-2 bg-solar-kinetic text-basalt-carbon px-10 py-4 rounded-full font-bold text-lg hover:bg-solar-kinetic/90 transition-all"
            >
              <span>{bandInfo.cta}</span>
              <ArrowRight size={20} />
            </a>
            <p className="text-sm text-ozone-white/70 mt-6">
              We've sent a copy of your results to <strong className="text-solar-kinetic">{lead.email}</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Quiz;
